import React, { useEffect, useRef, useState } from "react";
import { createChart, CrosshairMode, LineStyle } from "lightweight-charts";
import { getCandles, getProfile } from "../utils/stockApi.js";
import { CHART_RANGES } from "../utils/stockData.js";
import useAsync from "../utils/useAsync.js";

// TradingView-style chart built with TradingView's open-source Lightweight
// Charts: candles + volume, OHLC read-out, crosshair, timeframe bar.
// Hover for prices; the mouse wheel scrolls the PAGE; drag to pan.
// The 1D and 1W charts reload every minute so the newest candle appears.
const UP = "#089981";
const DOWN = "#f23645";
const LABELS = { "1d": "1D", "1w": "1W", "3m": "3M", "6m": "6M", ytd: "YTD", "1yr": "1Y", "5yr": "5Y", all: "All" };
const TYPES = [["candles", "Candles"], ["line", "Line"], ["area", "Area"]];
const LIVE_RANGES = ["1d", "1w"];
const REFRESH_MS = 60000;

const day = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// intraday bars use a UTC timestamp built from the exchange clock time, so the axis shows 9:30, 10:30...
const stamp = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes()) / 1000;
const when = (d, intraday) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
  (intraday ? ` ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "");
const f2 = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const vol = (n) => (n >= 1e9 ? `${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : `${(n / 1e3).toFixed(1)}K`);

export default function LightChart({ ticker }) {
  const hostRef = useRef(null);
  const chartRef = useRef(null);
  const mainRef = useRef(null);
  const volRef = useRef(null);
  const barsRef = useRef([]);
  const keyRef = useRef(""); // ticker|range|type of the last draw, to tell a refresh from a new view
  const [range, setRange] = useState("1yr");
  const [interval, setIntervalLabel] = useState("1W");
  const [intraday, setIntraday] = useState(false);
  const [type, setType] = useState("candles");
  const [tick, setTick] = useState(0); // bumped every minute on the 1D / 1W charts
  const [bar, setBar] = useState(null); // { o,h,l,c,v,prev,date }
  const profile = useAsync(() => getProfile(ticker), [ticker]);

  // create chart + volume once
  useEffect(() => {
    const host = hostRef.current;
    const chart = createChart(host, {
      width: host.clientWidth,
      height: host.clientHeight,
      layout: { background: { type: "solid", color: "#ffffff" }, textColor: "#131722", fontFamily: "inherit", fontSize: 12 },
      grid: { vertLines: { color: "#f0f3fa" }, horzLines: { color: "#f0f3fa" } },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "#9598a1", style: LineStyle.Dashed, width: 1, labelBackgroundColor: "#131722" },
        horzLine: { color: "#9598a1", style: LineStyle.Dashed, width: 1, labelBackgroundColor: "#131722" },
      },
      rightPriceScale: { borderColor: "#e0e3eb", scaleMargins: { top: 0.08, bottom: 0.28 } },
      timeScale: { borderColor: "#e0e3eb", timeVisible: false, rightOffset: 4 },
      handleScroll: { mouseWheel: false, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: false, pinch: true, axisPressedMouseMove: true },
    });
    const volume = chart.addHistogramSeries({ priceFormat: { type: "volume" }, priceScaleId: "vol", lastValueVisible: false, priceLineVisible: false });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    chartRef.current = chart;
    volRef.current = volume;

    chart.subscribeCrosshairMove((param) => {
      const bars = barsRef.current;
      if (!bars.length) return;
      let i = bars.length - 1;
      if (param.time && param.point) {
        const hit = bars.findIndex((b) => b.time === param.time);
        if (hit >= 0) i = hit;
      }
      const b = bars[i];
      setBar({ ...b, prev: i > 0 ? bars[i - 1].close : b.open });
    });

    const ro = new ResizeObserver(() => chart.applyOptions({ width: host.clientWidth, height: host.clientHeight }));
    ro.observe(host);
    return () => {
      ro.disconnect();
      chart.remove();
    };
  }, []);

  // 1D and 1W: reload every minute (only while the browser tab is visible)
  useEffect(() => {
    if (!LIVE_RANGES.includes(range)) return;
    const id = setInterval(() => {
      if (!document.hidden) setTick((t) => t + 1);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [range, ticker]);

  // load data
  useEffect(() => {
    let live = true;
    getCandles(ticker, range).then((res) => {
      if (!live || !chartRef.current || !res) return;
      setIntervalLabel(res.interval);
      setIntraday(res.intraday);
      chartRef.current.applyOptions({ timeScale: { timeVisible: res.intraday, secondsVisible: false } });
      const bars = res.bars.map((r) => ({ time: res.intraday ? stamp(r.t) : day(r.t), date: r.t, open: r.open, high: r.high, low: r.low, close: r.close, volume: r.volume }));
      barsRef.current = bars;
      if (!bars.length) {
        // no candles stored for this stock yet: show an empty chart instead of crashing
        volRef.current.setData([]);
        if (mainRef.current) { chartRef.current.removeSeries(mainRef.current); mainRef.current = null; }
        setBar(null);
        return;
      }

      volRef.current.setData(
        bars.map((b) => ({ time: b.time, value: b.volume, color: b.close >= b.open ? "rgba(8,153,129,0.35)" : "rgba(242,54,69,0.35)" }))
      );

      // (re)build the main series for the chosen chart type
      if (mainRef.current) chartRef.current.removeSeries(mainRef.current);
      let s;
      if (type === "candles") {
        s = chartRef.current.addCandlestickSeries({
          upColor: UP, downColor: DOWN, borderUpColor: UP, borderDownColor: DOWN, wickUpColor: UP, wickDownColor: DOWN,
        });
        s.setData(bars.map(({ time, open, high, low, close }) => ({ time, open, high, low, close })));
      } else if (type === "line") {
        s = chartRef.current.addLineSeries({ color: "#2962ff", lineWidth: 2 });
        s.setData(bars.map(({ time, close }) => ({ time, value: close })));
      } else {
        s = chartRef.current.addAreaSeries({ lineColor: "#2962ff", topColor: "rgba(41,98,255,0.28)", bottomColor: "rgba(41,98,255,0.02)", lineWidth: 2 });
        s.setData(bars.map(({ time, close }) => ({ time, value: close })));
      }
      mainRef.current = s;

      // fit the chart only when the view really changed; a minute-refresh keeps the user's pan/zoom
      const key = `${ticker}|${range}|${type}`;
      if (keyRef.current !== key) {
        keyRef.current = key;
        chartRef.current.timeScale().setVisibleLogicalRange({ from: -1.5, to: bars.length + 2 }); // small margin so the first and last candles aren't clipped
      }

      const last = bars[bars.length - 1];
      setBar({ ...last, prev: bars.length > 1 ? bars[bars.length - 2].close : last.open });
    }).catch(() => {
      if (live) setBar(null);
    });
    return () => {
      live = false;
    };
  }, [ticker, range, type, tick]);

  const chg = bar ? bar.close - bar.prev : 0;
  const chgPct = bar ? (chg / bar.prev) * 100 : 0;
  const color = chg >= 0 ? UP : DOWN;
  const p = profile.data;

  return (
    <div className="lc-wrap">
      <div className="lc-toolbar">
        <div className="lc-types">
          {TYPES.map(([k, l]) => (
            <button key={k} className={type === k ? "active" : ""} onClick={() => setType(k)}>{l}</button>
          ))}
        </div>
        <span className="lc-tf" title="Candle size">{interval}</span>
      </div>

      <div className="lc-stage">
        <div className="lc-host" ref={hostRef} />
        {bar && (
          <div className="lc-legend">
            <div className="lc-title">
              <b>{p ? p.name : ticker}</b> · {interval} {p ? `· ${p.exchange}` : ""} · {when(bar.date, intraday)}
            </div>
            <div className="lc-ohlc" style={{ color }}>
              <span>O <i>{f2(bar.open)}</i></span>
              <span>H <i>{f2(bar.high)}</i></span>
              <span>L <i>{f2(bar.low)}</i></span>
              <span>C <i>{f2(bar.close)}</i></span>
              <span><i>{chg >= 0 ? "+" : ""}{f2(chg)} ({chg >= 0 ? "+" : ""}{chgPct.toFixed(2)}%)</i></span>
            </div>
            <div className="lc-vol">Volume <i style={{ color }}>{vol(bar.volume)}</i></div>
          </div>
        )}
      </div>

      <div className="lc-ranges">
        {CHART_RANGES.map((r) => (
          <button key={r} className={r === range ? "active" : ""} onClick={() => setRange(r)}>{LABELS[r]}</button>
        ))}
      </div>
    </div>
  );
}